/**
 * The camps/clinics API as the shared components (components/typedEvents)
 * call it: NEUTRAL names (getTypedEvents, registerForTypedEvent, …) resolved
 * to the type's own module (campsAPI / clinicsAPI) at CALL time.
 *
 * Why through those modules rather than typedEventsAPI directly: they are the
 * seam the rest of the app — and every camps/clinics test — mocks
 * (`jest.mock("../../api/clinicsAPI", …)`). Going around them would silently
 * turn those mocks into no-ops and send tests to the network.
 *
 * Why at call time: a test mocks only the functions its page uses, and a type
 * may lack a capability (clinics have no batch registration). Resolving
 * eagerly would throw while a page is merely being built.
 *
 * The legacy names are listed explicitly — clinicsAPI exports mostly
 * camp-named functions (it began as a copy of campsAPI) — so this table, not a
 * string rule, is the source of truth. Generated from the modules' exports.
 */
import * as campsAPI from "./campsAPI";
import * as clinicsAPI from "./clinicsAPI";
import * as publicCampsAPI from "./publicCampsAPI";
import * as publicClinicsAPI from "./publicClinicsAPI";
import { byType, TypedEventType } from "../utils/typedEvents";

const LEGACY_NAMES: Record<TypedEventType, Record<string, string>> = {
  camp: {
    createTypedEvent: "createCamp",
    getTypedEvents: "getCamps",
    getTypedEventById: "getCampById",
    updateTypedEvent: "updateCamp",
    deleteTypedEvent: "deleteCamp",
    registerForTypedEvent: "registerForCamp",
    getTypedEventRegistrations: "getCampRegistrations",
    getMyTypedEventRegistrations: "getMyCampRegistrations",
    getTypedEventCancellationPreview: "getCampCancellationPreview",
    cancelTypedEventRegistration: "cancelCampRegistration",
    exportTypedEventRegistrations: "exportCampRegistrations",
    getTypedEventAttendance: "getCampAttendance",
    checkInTypedEventAttendee: "checkInCampAttendee",
    checkOutTypedEventAttendee: "checkOutCampAttendee",
    markTypedEventAttendeeAbsent: "markAttendeeAbsent",
    undoTypedEventCheckIn: "undoAttendeeCheckIn",
    duplicateTypedEvent: "duplicateCamp",
    getRegistrationQRCode: "getRegistrationQRCode",
    validateTypedEventQRCode: "validateCampQRCode",
    getTypedEventTemplates: "getCampTemplates",
    getTypedEventTemplateById: "getCampTemplateById",
    createTypedEventTemplate: "createCampTemplate",
    updateTypedEventTemplate: "updateCampTemplate",
    deleteTypedEventTemplate: "deleteCampTemplate",
    createTypedEventFromTemplate: "createCampFromTemplate",
    getTypedEventWaitlist: "getCampWaitlist",
    promoteFromWaitlist: "promoteFromWaitlist",
    getTypedEventStaff: "getCampStaff",
    getAvailableStaff: "getAvailableStaff",
    assignTypedEventStaff: "assignCampStaff",
    updateTypedEventStaffRole: "updateCampStaffRole",
    removeTypedEventStaff: "removeCampStaff",
    getAttendanceReport: "getAttendanceReport",
    getTypedEventAnalytics: "getCampAnalytics",
    getTypedEventStats: "getCampStats",
    sendTypedEventReminders: "sendCampReminders",
    sendTypedEventNotification: "sendCampNotification",
    getDailyUpdates: "getDailyUpdates",
    saveDailyUpdate: "saveDailyUpdate",
    sendDailyUpdateEmails: "sendDailyUpdateEmails",
    createTypedEventPaymentIntent: "createCampPaymentIntent",
    confirmTypedEventPayment: "confirmCampPayment",
    processTypedEventRefund: "processCampRefund",
    batchRegisterForTypedEvent: "batchRegisterForCamp",
    confirmBatchTypedEventPayment: "confirmBatchCampPayment",
    getTypedEventPaymentStatus: "getCampPaymentStatus",
    adminUpdateTypedEventPaymentStatus: "adminUpdateCampPaymentStatus",
    recordTypedEventRegistrationPayment: "recordCampRegistrationPayment",
    getTypedEventPhotos: "getCampPhotos",
    addTypedEventPhoto: "addCampPhoto",
    addTypedEventPhotos: "addCampPhotos",
    updateTypedEventPhoto: "updateCampPhoto",
    deleteTypedEventPhoto: "deleteCampPhoto",
    setFeaturedTypedEventPhoto: "setFeaturedCampPhoto",
    reorderTypedEventPhotos: "reorderCampPhotos",
    getTypedEventSupplyList: "getCampSupplyList",
    updateTypedEventSupplyList: "updateCampSupplyList",
    addSupplyItem: "addSupplyItem",
    sendBulkTypedEventRegistrationEmail: "sendBulkCampRegistrationEmail",
    removeSupplyItem: "removeSupplyItem",
    adminRegisterForTypedEvent: "adminRegisterForCamp",
  },
  clinic: {
    createTypedEvent: "createCamp",
    getTypedEvents: "getCamps",
    getTypedEventById: "getCampById",
    updateTypedEvent: "updateCamp",
    deleteTypedEvent: "deleteCamp",
    registerForTypedEvent: "registerForCamp",
    getTypedEventRegistrations: "getCampRegistrations",
    getMyTypedEventRegistrations: "getMyCampRegistrations",
    cancelTypedEventRegistration: "cancelCampRegistration",
    getTypedEventCancellationPreview: "getClinicCancellationPreview",
    exportTypedEventRegistrations: "exportCampRegistrations",
    getTypedEventAttendance: "getCampAttendance",
    checkInTypedEventAttendee: "checkInCampAttendee",
    checkOutTypedEventAttendee: "checkOutCampAttendee",
    markTypedEventAttendeeAbsent: "markAttendeeAbsent",
    undoTypedEventCheckIn: "undoAttendeeCheckIn",
    duplicateTypedEvent: "duplicateCamp",
    getRegistrationQRCode: "getRegistrationQRCode",
    validateTypedEventQRCode: "validateCampQRCode",
    getTypedEventTemplates: "getCampTemplates",
    getTypedEventTemplateById: "getCampTemplateById",
    createTypedEventTemplate: "createCampTemplate",
    updateTypedEventTemplate: "updateCampTemplate",
    deleteTypedEventTemplate: "deleteCampTemplate",
    createTypedEventFromTemplate: "createCampFromTemplate",
    getTypedEventWaitlist: "getCampWaitlist",
    promoteFromWaitlist: "promoteFromWaitlist",
    getTypedEventStaff: "getCampStaff",
    getAvailableStaff: "getAvailableStaff",
    assignTypedEventStaff: "assignCampStaff",
    updateTypedEventStaffRole: "updateCampStaffRole",
    removeTypedEventStaff: "removeCampStaff",
    getAttendanceReport: "getAttendanceReport",
    getTypedEventAnalytics: "getCampAnalytics",
    getTypedEventStats: "getCampStats",
    sendTypedEventReminders: "sendCampReminders",
    sendTypedEventNotification: "sendCampNotification",
    getDailyUpdates: "getDailyUpdates",
    saveDailyUpdate: "saveDailyUpdate",
    sendDailyUpdateEmails: "sendDailyUpdateEmails",
    createTypedEventPaymentIntent: "createCampPaymentIntent",
    confirmTypedEventPayment: "confirmCampPayment",
    processTypedEventRefund: "processCampRefund",
    getTypedEventPaymentStatus: "getCampPaymentStatus",
    adminUpdateTypedEventPaymentStatus: "adminUpdateCampPaymentStatus",
    recordTypedEventRegistrationPayment: "recordClinicRegistrationPayment",
    getTypedEventPhotos: "getCampPhotos",
    addTypedEventPhoto: "addCampPhoto",
    addTypedEventPhotos: "addCampPhotos",
    updateTypedEventPhoto: "updateCampPhoto",
    deleteTypedEventPhoto: "deleteCampPhoto",
    setFeaturedTypedEventPhoto: "setFeaturedCampPhoto",
    reorderTypedEventPhotos: "reorderCampPhotos",
    getTypedEventSupplyList: "getCampSupplyList",
    updateTypedEventSupplyList: "updateCampSupplyList",
    addSupplyItem: "addSupplyItem",
    sendBulkTypedEventRegistrationEmail: "sendBulkClinicRegistrationEmail",
    removeSupplyItem: "removeSupplyItem",
    adminRegisterForTypedEvent: "adminRegisterForClinic",
    batchRegisterForTypedEvent: "batchRegisterForClinic",
    confirmBatchTypedEventPayment: "confirmBatchClinicPayment",
  },
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ApiFunction = (...args: any[]) => Promise<any>;
export type TypedEventsApi = Record<string, ApiFunction>;

/** A proxy whose every property is a call-time lookup into `mod`. */
function resolvingProxy(
  type: TypedEventType,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  mod: Record<string, any>,
  names: Record<string, string>,
  moduleName: string
): TypedEventsApi {
  return new Proxy({} as TypedEventsApi, {
    get(_target, key) {
      if (typeof key !== "string") return undefined;
      return (...args: unknown[]) => {
        const legacy = names[key];
        if (!legacy) throw new Error(`${type}: no API function "${key}"`);
        const fn = mod[legacy];
        if (typeof fn !== "function") throw new Error(`${moduleName}.${legacy} is not a function`);
        return fn(...args);
      };
    },
  });
}

export function typedEventsApiFor(type: TypedEventType): TypedEventsApi {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const mod = byType<Record<string, any>>(type, { camp: campsAPI, clinic: clinicsAPI });
  return resolvingProxy(type, mod, LEGACY_NAMES[type], `${type}sAPI`);
}

// The public (unauthenticated) client, same pattern: resolved through
// publicCampsAPI / publicClinicsAPI at call time, so their mocks apply.

const PUBLIC_LEGACY_NAMES: Record<TypedEventType, Record<string, string>> = {
  camp: {
    lookupMemberByEmail: "lookupMemberByEmail",
    verifyMemberByEmail: "verifyMemberByEmail",
    getPublicTypedEvents: "getPublicCamps",
    getPublicTypedEventById: "getPublicCampById",
    registerGuestForTypedEvent: "registerGuestForCamp",
    confirmTypedEventPayment: "confirmCampPayment",
    batchRegisterGuestForTypedEvent: "batchRegisterGuestForCamp",
  },
  clinic: {
    lookupMemberByEmail: "lookupMemberByEmail",
    verifyMemberByEmail: "verifyMemberByEmail",
    getPublicTypedEvents: "getPublicClinics",
    getPublicTypedEventById: "getPublicClinicById",
    registerGuestForTypedEvent: "registerGuestForClinic",
    confirmTypedEventPayment: "confirmClinicPayment",
    batchRegisterGuestForTypedEvent: "batchRegisterGuestForClinic",
  },
};

export function publicTypedEventsApiFor(type: TypedEventType): TypedEventsApi {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const mod = byType<Record<string, any>>(type, { camp: publicCampsAPI, clinic: publicClinicsAPI });
  return resolvingProxy(type, mod, PUBLIC_LEGACY_NAMES[type], `public${type[0].toUpperCase()}${type.slice(1)}sAPI`);
}
